"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zodStringify = exports.zodParseAsync = exports.zodParse = void 0;
const zod_1 = require("zod");
const utils_1 = require("@azimutt/utils");
const zodParse = (typ, label) => (value) => {
    const res = typ.safeParse(value);
    return res.success ? utils_1.Result.success(res.data) : utils_1.Result.failure(zodErrorToString(res.error, typ, label, value));
};
exports.zodParse = zodParse;
const zodParseAsync = (typ, label) => (value) => (0, exports.zodParse)(typ, label)(value).mapError(e => new Error(e)).toPromise();
exports.zodParseAsync = zodParseAsync;
const zodStringify = (typ, label) => (value) => JSON.stringify((0, exports.zodParse)(typ, label)(value).getOrThrow());
exports.zodStringify = zodStringify;
const pathToString = (path) => path.length === 0 ? '_root_' : `.${path.join('.')}`;
const normalizePath = (path) => pathToString(path.map(p => typeof p === 'number' ? '?' : p));
function zodErrorToString(e, typ, label, value) {
    const name = label || typ.description || 'ZodType';
    const len = e.issues.length;
    if (len === 0) {
        return `Invalid ${name}, but no issue found...`;
    }
    else if (len === 1) {
        return `Invalid ${name}, ${zodIssueToString(e.issues[0], value)}`;
    }
    else if (len <= 10) {
        return `Invalid ${name}, ${len} issues:${e.issues.map(i => `\n- ${zodIssueToString(i, value)}`).join('')}`;
    }
    else {
        const issuesGroups = (0, utils_1.groupBy)(e.issues, i => i.message + ':' + normalizePath(i.path));
        const formattedGroups = Object.entries(issuesGroups).map(([_, [issue, ...others]]) => {
            if (others.length === 0) {
                return `\n- ${zodIssueToString(issue, value)}`;
            }
            else {
                return `\n- at ${normalizePath(issue.path)}: ${zodIssueMessageToString(issue, value)} (${pathToString(issue.path)} and ${others.length} more)`;
            }
        });
        return `Invalid ${name}, ${len} issues found in ${(0, utils_1.pluralizeL)(formattedGroups, 'group')}:${formattedGroups.join('')}`;
    }
}
function zodIssueToString(issue, value) {
    return `at ${pathToString(issue.path)}: ${zodIssueMessageToString(issue, value)}`;
}
function zodIssueMessageToString(issue, value) {
    if (issue.code === zod_1.z.ZodIssueCode.unrecognized_keys) {
        return `invalid additional key${issue.keys.length > 1 ? 's:' : ''} ${issue.keys.map(k => `'${k}' (${JSON.stringify((0, utils_1.getValueDeep)(value, issue.path.concat(k)))})`).join(', ')}`;
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_type) {
        return `expect '${issue.expected}' but got '${issue.received}' (${JSON.stringify((0, utils_1.getValueDeep)(value, issue.path))})`;
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_literal) {
        return `expect ${JSON.stringify(issue.expected)} but got ${JSON.stringify((0, utils_1.getValueDeep)(value, issue.path))}`;
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_enum_value) {
        return `expect \`${issue.options.map(o => JSON.stringify(o)).join(' | ')}\` but got ${JSON.stringify((0, utils_1.getValueDeep)(value, issue.path))}`;
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_union_discriminator) {
        return `expect \`${issue.options.map(o => JSON.stringify(o)).join(' | ')}\` but got ${JSON.stringify((0, utils_1.getValueDeep)(value, issue.path))}`;
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_union) {
        return `invalid union for ${JSON.stringify((0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), 3))}`;
    }
    else {
        return issue.message;
    }
}
function zodErrorToJson(error, value) {
    return error.issues.map(i => zodIssueToJson(i, value));
}
function zodIssueToJson(issue, value) {
    const depth = 2;
    if (issue.code === zod_1.z.ZodIssueCode.invalid_type) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, expected: issue.expected, received: issue.received };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_literal) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, expected: issue.expected };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.unrecognized_keys) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, keys: issue.keys };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_union) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, unionErrors: issue.unionErrors.map(e => zodErrorToJson(e, value)) };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_union_discriminator) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, options: issue.options };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_enum_value) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, options: issue.options, received: issue.received };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_arguments) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, argumentsError: issue.argumentsError };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_return_type) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, returnTypeError: issue.returnTypeError };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_date) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_string) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, validation: issue.validation };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.too_small) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, type: issue.type, minimum: issue.minimum, inclusive: issue.inclusive };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.too_big) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, type: issue.type, maximum: issue.maximum, inclusive: issue.inclusive };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.invalid_intersection_types) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.not_multiple_of) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, multipleOf: issue.multipleOf };
    }
    else if (issue.code === zod_1.z.ZodIssueCode.custom) {
        return { code: issue.code, path: issue.path, value: (0, utils_1.limitDepth)((0, utils_1.getValueDeep)(value, issue.path), depth), message: issue.message, params: issue.params };
    }
    else {
        throw `Unhandled ZodIssue!`;
    }
}
//# sourceMappingURL=zod.js.map